import { Button, Empty, Loader, Modal } from "rizzui";
import { useEffect, useState } from "react";
import PaymentRepository from "@/utilities/repositories/Payment.ts";
import { EStatusPayment, IItemsHistory } from "@/utilities/types/Payment.ts";

interface IPaymentHistoryProps {
  isModalOpen: boolean;
  closeModal: () => void;
  xeroInvoiceID: string;
}

const headers = [
  "Xero Invoice ID",
  "Online Invoice URL",
  "Status",
  "Created At",
  "Sub title",
  "Discount",
  "Total Paid",
  "Total Due",
];

const StatusPayment = Object.entries(EStatusPayment).reduce(
  (acc, [label, value]) => {
    acc[value] = label;
    return acc;
  },
  {} as Record<string, string>
);

const PaymentHistoryTable = ({ data }: { data: IItemsHistory[] }) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto border-collapse border border-gray-200">
        <thead className="bg-gray-100 border-b border-gray-200">
          <tr>
            {headers?.map((header) => (
              <th
                key={header}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((item) => (
            <tr key={item?.id} className="border-b border-gray-200">
              <td className="px-4 py-2">{item.xeroPaymentID}</td>
              <td className="px-4 py-2">
                {item.onlineInvoiceUrl ? (
                  <a
                    href={item?.onlineInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                  >
                    View Invoice
                  </a>
                ) : (
                  "N/A"
                )}
              </td>
              <td className="px-4 py-2 text-nowrap">
                {StatusPayment[item?.status]}
              </td>
              <td className="px-4 py-2">
                {new Date(item?.createdAt).toLocaleString()}
              </td>
              <td className="px-4 py-2">{item.originPrice || "N/A"}</td>
              <td className="px-4 py-2">{item.discountPrice || "N/A"}</td>
              <td className="px-4 py-2">{item.paid || "N/A"}</td>
              <td className="px-4 py-2">{item.totalDue || "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PaymentHistoryModal = ({
  isModalOpen,
  closeModal,
  xeroInvoiceID,
}: IPaymentHistoryProps) => {
  const [paymentHistoryData, setPaymentHistoryData] = useState<IItemsHistory[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      setIsLoading(true);
      try {
        const res = await PaymentRepository.historyPaymentDetail(xeroInvoiceID);
        if (res?.success && res?.data?.items?.length) {
          setPaymentHistoryData(res.data.items);
        }
        setIsLoading(false);
      } catch (err) {
        console.log(err);
        setIsLoading(false);
      }
    };
    fetchPaymentDetails();
  }, [xeroInvoiceID]);
  return (
    <Modal isOpen={isModalOpen} onClose={() => closeModal()} size="xl">
      <div className="p-6 relative">
        <h2 className="text-2xl font-bold mb-6">History Detail</h2>
        <div className="w-full overflow-x-auto h-[500px]">
          {isLoading ? (
            <div className="absolute top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2">
              <Loader size="xl" />
            </div>
          ) : paymentHistoryData?.length > 0 ? (
            <PaymentHistoryTable data={paymentHistoryData} />
          ) : (
            <div className="absolute top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2">
              <Empty text="No Data" textClassName="mt-2" />
            </div>
          )}
        </div>
        <div className="w-full flex justify-end mt-6 z-50">
          <Button variant="outline" onClick={() => closeModal()}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentHistoryModal;
